<!-- src/apps/secret/conceal/HomepageContent.vue -->

<script setup lang="ts">
  import CtaSection from '@/apps/secret/components/conceal/landing/CtaSection.vue';
  import HeroPanel from '@/apps/secret/components/conceal/landing/HeroPanel.vue';
  import ProtocolSection from '@/apps/secret/components/conceal/landing/ProtocolSection.vue';
  import SocialProof from '@/apps/secret/components/conceal/landing/SocialProof.vue';
  import SpecsSection from '@/apps/secret/components/conceal/landing/SpecsSection.vue';
  import TerminalWindow from '@/apps/secret/components/conceal/landing/TerminalWindow.vue';
  import DashboardView from '@/apps/secret/components/dashboard/DashboardView.vue';
  import SecretForm from '@/apps/secret/components/form/SecretForm.vue';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useProductIdentity } from '@/shared/stores/identityStore';
  import { useLocalReceiptStore } from '@/shared/stores/localReceiptStore';
  import { storeToRefs } from 'pinia';

  const bootstrapStore = useBootstrapStore();
  const { authenticated, ui } = storeToRefs(bootstrapStore);

  // Resolve the active brand color so the CTA tracks the same source as the
  // logo (identityStore.primaryColor → --color-brand-500). Without this the
  // button falls back to SecretForm's neutral-blue default, so a branded
  // install shows a brand-colored logo above a neutral-blue button.
  const identityStore = useProductIdentity();
  const { primaryColor } = storeToRefs(identityStore);

  const localReceiptStore = useLocalReceiptStore();
</script>

<template>
  <!--
    seuid.com.br landing (Figma Make, ago/2026): mono-led terminal aesthetic,
    hairline borders. Anonymous visitors get the marketing landing; signed-in
    users get the Dashboard (privacy toolbar + editor panel + receipts ledger),
    which is a different screen in the mock, not a compacted landing.
  -->
  <!--
    applies the Outfit body face to the landing (headings opt into
    font-mono per element); dark:bg-night makes this page the near-black
    surface the panels (night-100/200) sit ON TOP of, matching the mock's
    surface hierarchy instead of the body's gray-900.
  -->
  <DashboardView v-if="authenticated" />

  <div
    v-else
    class="mx-auto w-full min-w-[320px] max-w-7xl dark:bg-night">
    <!-- ── HERO ── -->
    <section
      class="grid grid-cols-1 border-b border-day-line dark:border-white/[0.07] lg:grid-cols-2">
      <div
        class="border-b border-day-line p-5 py-12 dark:border-white/[0.07] sm:p-8 lg:border-b-0 lg:border-r lg:py-20 lg:pr-14">
        <HeroPanel />
      </div>

      <div class="flex flex-col justify-center p-5 py-10 sm:p-8 lg:py-14 lg:pl-12">
        <TerminalWindow v-if="ui?.enabled !== false">
          <SecretForm
            chromeless
            corner-class="rounded-none"
            :with-recipient="false"
            :with-asterisk="true"
            :with-generate="true"
            :primary-color="primaryColor"
            :workspace-mode="localReceiptStore.workspaceMode" />
        </TerminalWindow>
      </div>
    </section>

    <!-- ── SOCIAL PROOF (anonymous only): real counter + trust seals ── -->
    <SocialProof />

    <!-- ── MARKETING (anonymous only) ── -->
    <ProtocolSection />
    <SpecsSection />
    <CtaSection />
  </div>
</template>
